import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { PageBody } from '../components/layout/PageBody';
import { ContentBlocks } from '../components/article/ContentBlocks';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorAlert } from '../components/common/ErrorAlert';
import { LoadingState } from '../components/common/LoadingState';
import { EngagementPeopleDrawer } from '../components/common/EngagementPeopleDrawer';
import { ReactionBar } from '../components/common/ReactionBar';
import { useFeedback } from '../components/common/FeedbackProvider';
import { useArticle } from '../hooks/useArticle';
import { useAuth } from '../hooks/useAuth';
import { canApprove, canViewAnalytics } from '../utils/roles';
import { shareContent } from '../utils/share';
import { viewerReactionFromArticle } from '../utils/articleReactions';
import { categoryLabel, displayTitle } from '../utils/labels';
import { formatDate } from '../utils/format';
import { useSmartBack } from '../hooks/useSmartBack';
import { backLabel } from '../utils/navigation';
import { alpha, tokens } from '../theme/tokens';

function AdminEngagementCard({ engagement, onOpenPeople }) {
  if (!engagement) return null;
  const liked = engagement.likedCount ?? 0;
  const sharedBy = engagement.sharedByCount ?? engagement.shareCount ?? 0;

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 1.75, sm: 2.25 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(tokens.primary, 0.03),
      }}
    >
      <Typography variant="h6" sx={{ mb: 0.5, fontSize: { xs: '1.05rem', sm: '1.15rem' } }}>
        Post engagement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75 }}>
        Admin only · {engagement.viewCount ?? 0} views · {engagement.uniqueReach ?? 0}{' '}
        reach · {sharedBy} shares
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onOpenPeople('likes')}
        >
          Liked by {liked.toLocaleString()}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onOpenPeople('shares')}
        >
          Shared by {sharedBy.toLocaleString()}
        </Button>
      </Stack>
    </Box>
  );
}

export function ArticlePage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useSmartBack('/');
  const { showSuccess, showError } = useFeedback();
  const {
    article,
    loading,
    error,
    busy,
    setError,
    react,
    share,
    comment,
    deleteComment,
  } = useArticle(id);
  const [commentText, setCommentText] = useState('');
  const [peopleDrawer, setPeopleDrawer] = useState(null);
  const showAdminEngagement = canViewAnalytics(user?.role);

  async function onReact(emoji) {
    if (!user) {
      navigate('/login', {
        state: {
          from: `/article/${id}`,
          authMessage: 'Sign in to like stories',
        },
      });
      return;
    }
    try {
      await react(emoji);
      showSuccess(emoji ? 'Liked' : 'Like removed');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Like failed');
    }
  }

  async function onComment(e) {
    e.preventDefault();
    if (!user) {
      navigate('/login', {
        state: {
          from: `/article/${id}`,
          authMessage: 'Sign in to comment',
        },
      });
      return;
    }
    if (!commentText.trim()) {
      showError('Write a comment first');
      return;
    }
    try {
      await comment(commentText.trim());
      setCommentText('');
      showSuccess('Comment posted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Comment failed');
    }
  }

  async function onDeleteComment(commentId) {
    try {
      await deleteComment(commentId);
      showSuccess('Comment removed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not delete comment');
    }
  }

  const canModerateComments = canApprove(user?.role);

  async function onShare() {
    const url = article?.url || `${window.location.origin}/article/${id}`;
    try {
      const result = await shareContent({
        title: article?.title || 'Prasaar',
        text: article?.summary || '',
        url,
      });
      if (result === 'cancelled') return;
      if (user) {
        try {
          await share();
        } catch {
          /* share still succeeded locally */
        }
      }
      showSuccess(result === 'shared' ? 'Shared' : 'Link copied');
    } catch {
      showError('Could not share');
    }
  }

  return (
    <AppShell
      title="Story"
      subtitle={loading ? 'Loading…' : undefined}
      maxWidth="md"
    >
      <PageBody>
        <ErrorAlert
          message={!loading && !article ? null : error}
          onClose={() => setError(null)}
        />
        {loading && <LoadingState label="Loading article…" />}

        {!loading && !article && (
          <EmptyState
            title="This story is no longer on the feed"
            description="It may have been taken down after this alert was sent. You can go back — the alert is still in your list."
            action={
              <Button variant="outlined" onClick={goBack}>
                {location.state?.from ? `Back to ${backLabel(location.state.from)}` : 'Back to feed'}
              </Button>
            }
          />
        )}

        {article && (
          <Card
            sx={{
              borderColor: alpha(tokens.border, 0.95),
              boxShadow: `0 10px 36px ${alpha(tokens.ink, 0.05)}`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: 4,
                background: tokens.brandFill,
              }}
            />
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, pb: { xs: 10, sm: 8 } }}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: { xs: 1.25, sm: 1.75 } }}
              >
                {(article.categories ?? []).map((c) => (
                  <Chip
                    key={c}
                    size="small"
                    color="secondary"
                    label={categoryLabel(c)}
                    clickable
                    component={RouterLink}
                    to={`/?category=${encodeURIComponent(c)}`}
                    replace
                  />
                ))}
              </Stack>

              {(article.publishedAt || article.sourceName) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  {[formatDate(article.publishedAt), article.sourceName]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}

              <Typography
                variant="h3"
                component="h2"
                sx={{
                  mb: 1.25,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.2,
                  fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2.15rem' },
                }}
              >
                {displayTitle(article.title)}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  mb: { xs: 2.5, sm: 3.5 },
                  lineHeight: 1.65,
                  fontSize: { xs: '1.05rem', sm: '1.125rem' },
                }}
              >
                {article.summary}
              </Typography>

              <Box
                sx={{
                  '& .content-html': {
                    fontSize: '1.05rem',
                    lineHeight: 1.7,
                    color: 'text.primary',
                  },
                }}
              >
                <ContentBlocks
                  blocks={article.blocks}
                  content={article.content}
                />
              </Box>

              <Box
                sx={{
                  mt: 3.5,
                  position: 'sticky',
                  bottom:
                    'calc(16px + var(--pwa-banner-offset, 0px) + var(--safe-bottom))',
                  zIndex: 1,
                  p: 1.25,
                  borderRadius: 2.5,
                  bgcolor: alpha(tokens.surface, 0.92),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: `0 8px 24px ${alpha(tokens.ink, 0.08)}`,
                  width: 'fit-content',
                  maxWidth: '100%',
                }}
              >
                <ReactionBar
                  myReaction={viewerReactionFromArticle(article)}
                  reactionCounts={article.reactions}
                  shareCount={article.shareCount ?? 0}
                  onReact={onReact}
                  onShare={onShare}
                  disabled={busy}
                />
              </Box>

              {showAdminEngagement && article.engagement && (
                <AdminEngagementCard
                  engagement={article.engagement}
                  onOpenPeople={(kind) => setPeopleDrawer(kind)}
                />
              )}

              <EngagementPeopleDrawer
                open={Boolean(peopleDrawer)}
                onClose={() => setPeopleDrawer(null)}
                articleId={id}
                kind={peopleDrawer || 'likes'}
              />

              <Divider sx={{ my: 3.5, mb: 3.5 }} />

              <Typography variant="h5" sx={{ mb: 2 }}>
                Comments
              </Typography>

              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {(article.comments ?? []).map((c) => (
                  <Box
                    key={c.id}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: alpha(tokens.primary, 0.035),
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Typography fontWeight={700} sx={{ mb: 0.35 }}>
                        {c.userName}
                      </Typography>
                      {canModerateComments && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => void onDeleteComment(c.id)}
                          disabled={busy}
                        >
                          Delete
                        </Button>
                      )}
                    </Stack>
                    <Typography sx={{ lineHeight: 1.55 }}>{c.text}</Typography>
                  </Box>
                ))}
                {(article.comments ?? []).length === 0 && (
                  <Typography color="text.secondary">No comments yet.</Typography>
                )}
              </Stack>

              <Box component="form" onSubmit={onComment}>
                <TextField
                  label="Add a comment"
                  multiline
                  rows={3}
                  fullWidth
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={user ? 'Share your take…' : 'Sign in to comment'}
                  disabled={!user}
                  sx={{ mb: 2 }}
                />
                <Button
                  type={user ? 'submit' : 'button'}
                  variant="contained"
                  disabled={busy}
                  onClick={
                    user
                      ? undefined
                      : () =>
                          navigate('/login', {
                            state: {
                              from: `/article/${id}`,
                              authMessage: 'Sign in to comment',
                            },
                          })
                  }
                >
                  {user ? 'Post comment' : 'Sign in to comment'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </PageBody>
    </AppShell>
  );
}
